import React, { createContext, useContext, useState, useEffect } from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { getSupabase } from '../lib/supabase';
import { logger } from '../lib/logger';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isLoading: boolean;
  ensureAuthReady: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ data: any; error: AuthError | null }>;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<{ data: any; error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ data: any; error: AuthError | null }>;
  updatePassword: (password: string) => Promise<{ data: any; error: AuthError | null }>;
  storePendingSubscription: (productId: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function needsImmediateAuth(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return /\/(checkout|_console|admin|auth)\b/i.test(p);
}

/** Landings SEO — no supabase until interaction (PSI Lighthouse window). */
function isSeoLandingPath(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return /\/(compare|use-case|replace)(\/|$)/i.test(p);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  // Anonymous-first: not loading until we choose to init (CWV — no supabase on home paint)
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    let unsubscribe = () => {};

    const init = async () => {
      if (!mounted) return;
      setLoading(true);
      try {
        const supabase = await getSupabase();
        if (!mounted) return;

        const {
          data: { session: initial },
          error,
        } = await supabase.auth.getSession();
        if (error) throw error;

        if (mounted) {
          setUser(initial?.user ?? null);
          setSession(initial);
          setLoading(false);
          setReady(true);
        }

        const { data } = supabase.auth.onAuthStateChange((_event: string, next: Session | null) => {
          if (!mounted) return;
          setUser(next?.user ?? null);
          setSession(next);
          setLoading(false);
        });
        unsubscribe = () => data.subscription.unsubscribe();
      } catch (error) {
        logger.error('Error getting session:', error);
        if (mounted) {
          setUser(null);
          setSession(null);
          setLoading(false);
          setReady(true);
        }
      }
    };

    const schedule = () => {
      if (needsImmediateAuth()) {
        void init();
        return;
      }

      // Marketing landings: wait for first interaction OR long idle (outside typical Lighthouse window)
      if (isSeoLandingPath()) {
        let done = false;
        const events = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;
        const run = () => {
          if (done) return;
          done = true;
          events.forEach((e) => window.removeEventListener(e, onInteract, true));
          window.clearTimeout(longTimer);
          void init();
        };
        const onInteract = () => run();
        events.forEach((e) => window.addEventListener(e, onInteract, { once: true, passive: true, capture: true }));
        const longTimer = window.setTimeout(run, 20000);
        return () => {
          done = true;
          events.forEach((e) => window.removeEventListener(e, onInteract, true));
          window.clearTimeout(longTimer);
        };
      }

      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
        cancelIdleCallback?: (id: number) => void;
      };
      // Non-landing marketing: idle with long timeout (was 5s — still in PSI critical path)
      if (typeof w.requestIdleCallback === 'function') {
        const id = w.requestIdleCallback(() => void init(), { timeout: 15000 });
        return () => w.cancelIdleCallback?.(id);
      }
      const t = window.setTimeout(() => void init(), 12000);
      return () => clearTimeout(t);
    };

    const cancel = schedule();
    return () => {
      mounted = false;
      cancel?.();
      unsubscribe();
    };
  }, []);

  const ensureAuthReady = async () => {
    if (ready) return;
    setLoading(true);
    const supabase = await getSupabase();
    const {
      data: { session: s },
    } = await supabase.auth.getSession();
    setUser(s?.user ?? null);
    setSession(s);
    setLoading(false);
    setReady(true);
  };

  const signIn = async (email: string, password: string) => {
    try {
      await ensureAuthReady();
      const supabase = await getSupabase();
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      return { data, error };
    } catch (error) {
      logger.error('Sign in error:', error);
      return { data: null, error: error as AuthError };
    }
  };

  const signUp = async (email: string, password: string, metadata?: Record<string, string>) => {
    try {
      await ensureAuthReady();
      const supabase = await getSupabase();
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: metadata,
        },
      });
      return { data, error };
    } catch (error) {
      logger.error('Sign up error:', error);
      return { data: null, error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error) {
      logger.error('Sign out error:', error);
      return { error: error as AuthError };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await ensureAuthReady();
      const supabase = await getSupabase();
      const redirectTo = `${window.location.origin}/auth/reset-password/`;
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });
      return { data, error };
    } catch (error) {
      logger.error('Reset password error:', error);
      return { data: null, error: error as AuthError };
    }
  };

  const updatePassword = async (password: string) => {
    try {
      await ensureAuthReady();
      const supabase = await getSupabase();
      const { data, error } = await supabase.auth.updateUser({ password });
      return { data, error };
    } catch (error) {
      logger.error('Update password error:', error);
      return { data: null, error: error as AuthError };
    }
  };

  const storePendingSubscription = (productId: string) => {
    localStorage.setItem('pending_subscription', productId);
  };

  const value: AuthContextValue = {
    user,
    session,
    loading,
    isLoading: loading,
    ensureAuthReady,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    storePendingSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
